import { RefObject, useEffect, useRef } from 'react';

import { applyQuietZone, buildQrCodeOptions, QrConfig } from 'editor/src/lib/qr';

import type QRCodeStyling from 'qr-code-styling';

const PREVIEW_SIZE = 240;
const DEBOUNCE_MS = 200;

/**
 * Renders a live, debounced QR preview into `container` whenever `config` changes.
 * Uses a single qr-code-styling instance and `.update()` for smooth re-renders.
 */
export default function useQrPreview(container: RefObject<HTMLElement>, config: QrConfig, enabled: boolean): void {
  const instanceRef = useRef<QRCodeStyling | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!enabled || !container.current) {
      return undefined;
    }

    const previewConfig = { ...config, size: PREVIEW_SIZE };
    const options = buildQrCodeOptions(previewConfig, 'canvas');
    const node = container.current;

    const render = async () => {
      const { default: QRCodeStyling } = await import('qr-code-styling');
      if (!instanceRef.current) {
        instanceRef.current = new QRCodeStyling(options);
        node.innerHTML = '';
        instanceRef.current.append(node);
      } else {
        instanceRef.current.update(options);
      }
      applyQuietZone(instanceRef.current, previewConfig);
    };

    timerRef.current = setTimeout(() => {
      void render();
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(config)]);

  // Drop the instance when the preview unmounts / is disabled so it re-creates cleanly next open.
  useEffect(() => {
    if (!enabled) {
      instanceRef.current = null;
    }
  }, [enabled]);
}
